import { NETWORK_COLORS } from '@/constants/colors'
import { BENCHMARK_SELECTED_NETWORKS_COOKIE_KEY } from '@/constants/cookie'
import { useAllNetworksBenchmarksQuery } from '@/queries/allNetworksBenchmarks.query'
import type { VaultApyRange } from '@/types/apy'
import type { ChartPeriod } from '@/types/periods'
import { getApyValue } from '@/utils/apy'
import { getCookieValue } from '@/utils/cookie/cookie'
import { subDays, toUnixTimestamp } from '@vaultsfyi/common/utils'
import { useEffect, useMemo, useRef } from 'react'
import { BenchmarkNetworksChart } from './benchmarkNetworksChart'

interface Props {
  apyRange: VaultApyRange
  includeRewards: boolean
  chartPeriod: ChartPeriod
  selectedNetworks: Set<string>
  onSelectedNetworksChange: (networks: Set<string>) => void
}

export const AnalyticsBenchmarks = ({
  apyRange,
  includeRewards,
  chartPeriod,
  selectedNetworks,
  onSelectedNetworksChange,
}: Props) => {
  const hasInitialized = useRef(false)

  const { data: usdData } = useAllNetworksBenchmarksQuery({ code: 'usd' })
  const { data: ethData } = useAllNetworksBenchmarksQuery({ code: 'eth' })

  const allNetworks = useMemo(() => {
    const networks = new Set([...Object.keys(usdData ?? {}), ...Object.keys(ethData ?? {})])
    return Array.from(networks)
  }, [usdData, ethData])

  const colorMap = useMemo(() => {
    const map: Record<string, string> = {}
    for (const network of allNetworks) {
      const color = NETWORK_COLORS[network as keyof typeof NETWORK_COLORS]
      if (color) map[network] = color
    }
    return map
  }, [allNetworks])

  useEffect(() => {
    if ((usdData || ethData) && !hasInitialized.current) {
      hasInitialized.current = true
      const saved = getCookieValue(BENCHMARK_SELECTED_NETWORKS_COOKIE_KEY)
      if (saved) {
        try {
          const parsed = JSON.parse(saved) as string[]
          const valid = parsed.filter((n) => allNetworks.includes(n))
          if (valid.length > 0) {
            onSelectedNetworksChange(new Set(valid))
            return
          }
        } catch {}
      }
      onSelectedNetworksChange(new Set(['mainnet']))
    }
  }, [usdData, ethData, allNetworks, onSelectedNetworksChange])

  const sharedYDomain = useMemo(() => {
    const cutoff = Math.floor(toUnixTimestamp(subDays(Date.now(), chartPeriod)))
    const values = [usdData, ethData].flatMap((data) =>
      [...selectedNetworks].flatMap((network) =>
        (data?.[network] ?? [])
          .filter((p) => p.timestamp >= cutoff && p.apy)
          .map((p) => getApyValue({ apy: p.apy!, apyRange, includeRewards }))
      )
    )
    if (values.length === 0) return undefined
    const min = values.reduce((a, b) => Math.min(a, b), Infinity)
    const max = values.reduce((a, b) => Math.max(a, b), -Infinity)
    const step = 0.02
    return [Math.floor(min / step) * step, Math.ceil(max / step) * step] as [number, number]
  }, [usdData, ethData, selectedNetworks, chartPeriod, apyRange, includeRewards])

  return (
    <div className="flex flex-grow flex-col justify-evenly gap-4">
      <BenchmarkNetworksChart
        code="usd"
        layout="compact"
        apyRange={apyRange}
        includeRewards={includeRewards}
        period={chartPeriod}
        selectedNetworks={selectedNetworks}
        colorMap={colorMap}
        yDomain={sharedYDomain}
      />
      <BenchmarkNetworksChart
        code="eth"
        layout="compact"
        apyRange={apyRange}
        includeRewards={includeRewards}
        period={chartPeriod}
        selectedNetworks={selectedNetworks}
        colorMap={colorMap}
        yDomain={sharedYDomain}
      />
    </div>
  )
}
